/**
 * Artefact registry (procedural memory). Mirrors the PRD client: column-
 * driven list filters, typed Create/Update/Response, and a REQUIRED If-Match
 * (the row's metadata_version) on every metadata, content and status write
 * for optimistic concurrency. The review moves (submit, withdraw, approve)
 * carry the revision they acted on in their body instead, beside the
 * directive the audit row and the release record.
 */

import type {
  ArtifactBundleRequest,
  ArtifactBundleResponse,
  ArtifactBundleUpdate,
  ArtifactCreate,
  ArtifactDownloadResponse,
  ArtifactListFilters,
  ArtifactListResponse,
  ArtifactMetadataResponse,
  ArtifactResponse,
  ArtifactUpdate,
} from '@/types/artifacts'
import type {
  ArtifactApproveRequest,
  ArtifactStatusUpdate,
  ArtifactTransitionRequest,
  ReleaseListResponse,
} from '@/types/artifactReleases'
import type {
  ArtifactValidationReport,
  ArtifactValidationRequest,
} from '@/types/artifactValidation'
import type {
  ArtifactRollbackRequest,
  ArtifactVersionListResponse,
  ArtifactVersionResponse,
} from '@/types/artifactVersions'
import type {
  ArtifactProfileMembership,
  ProfileCreate,
  ProfileMembership,
  ProfileRepinPreview,
  ProfileRepinRequest,
  ProfileResponse,
  ProfileRollbackRequest,
  ProfileSummary,
  ProfileVersionResponse,
} from '@/types/bootstrapProfiles'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'

export const proceduralMemory = {
  list: async (filters: ArtifactListFilters): Promise<ArtifactListResponse> => {
    const response = await apiClient.get<ArtifactListResponse>(
      '/api/v1/procedural-memory/ui/',
      { params: filters, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  // An operator reads the editable head and anyone else the published
  // release; `revision: 'published'` asks for the release whoever asks, which
  // is how a reviewer reads what is served beside the head awaiting approval.
  get: async (
    id: string,
    options?: { revision?: 'published' }
  ): Promise<ArtifactResponse> => {
    const response = await apiClient.get<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/${id}`,
      { params: { revision: options?.revision } }
    )
    return response.data
  },

  // Resolves a deep-link artefact name to its canonical row. Name is non-unique
  // (composite natural key); the backend applies the deterministic visibility
  // tiebreak and 404s a miss.
  getByName: async (name: string): Promise<ArtifactResponse> => {
    const response = await apiClient.get<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/by-name/${encodeURIComponent(name)}`
    )
    return response.data
  },

  getMetadata: async (): Promise<ArtifactMetadataResponse> => {
    const response = await apiClient.get<ArtifactMetadataResponse>(
      '/api/v1/procedural-memory/ui/metadata'
    )
    return response.data
  },

  // JSON body on the shared apiClient (not FormData); no If-Match — a create
  // has no prior version. Mirrors prds.create.
  create: async (data: ArtifactCreate): Promise<ArtifactResponse> => {
    const response = await apiClient.post<ArtifactResponse>(
      '/api/v1/procedural-memory/ui/',
      data
    )
    return response.data
  },

  // `ifMatch` is REQUIRED on every mutation below — the metadata_version of
  // the row being changed; the server returns 412 on a stale value.
  update: async (
    id: string,
    data: ArtifactUpdate,
    ifMatch: number
  ): Promise<ArtifactResponse> => {
    const response = await apiClient.patch<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/${id}`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  updateBundle: async (
    id: string,
    data: ArtifactBundleUpdate,
    ifMatch: number
  ): Promise<ArtifactResponse> => {
    const response = await apiClient.put<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/${id}/bundle`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  // Retires a head only: releasing is an approval and a draft is reached by
  // withdrawing, so neither is a status this route sets.
  setStatus: async (
    id: string,
    status: ArtifactStatusUpdate['status'],
    directive: string,
    ifMatch: number
  ): Promise<ArtifactResponse> => {
    const body: ArtifactStatusUpdate = { status, directive }
    const response = await apiClient.post<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/${id}/status`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  // 422 names every content-rule finding; the rules run before the move.
  submit: async (
    id: string,
    body: ArtifactTransitionRequest
  ): Promise<ArtifactResponse> => {
    const response = await apiClient.post<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/${id}/submit`,
      body
    )
    return response.data
  },

  withdraw: async (
    id: string,
    body: ArtifactTransitionRequest
  ): Promise<ArtifactResponse> => {
    const response = await apiClient.post<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/${id}/withdraw`,
      body
    )
    return response.data
  },

  // An administrator's decision in the console: a CLI credential gets 403.
  // 422 when the rules fail again; 409 when the version names an existing
  // release or does not advance past the highest one.
  approve: async (
    id: string,
    body: ArtifactApproveRequest
  ): Promise<ArtifactResponse> => {
    const response = await apiClient.post<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/${id}/approve`,
      body
    )
    return response.data
  },

  // The content rules over a bundle nobody has stored; writes nothing.
  validate: async (
    body: ArtifactValidationRequest
  ): Promise<ArtifactValidationReport> => {
    const response = await apiClient.post<ArtifactValidationReport>(
      '/api/v1/procedural-memory/ui/validate',
      body
    )
    return response.data
  },

  listReleases: async (id: string): Promise<ReleaseListResponse> => {
    const response = await apiClient.get<ReleaseListResponse>(
      `/api/v1/procedural-memory/ui/${id}/releases`
    )
    return response.data
  },

  // Every profile that lists the artefact, pins it, or both; a viewer's read.
  listProfileMemberships: async (
    id: string
  ): Promise<ArtifactProfileMembership[]> => {
    const response = await apiClient.get<ArtifactProfileMembership[]>(
      `/api/v1/procedural-memory/ui/${id}/profiles`
    )
    return response.data
  },

  delete: async (id: string, ifMatch: number): Promise<void> => {
    await apiClient.delete(
      `/api/v1/procedural-memory/ui/${id}`,
      ifMatchHeaders(ifMatch)
    )
  },

  download: async (id: string): Promise<ArtifactDownloadResponse> => {
    const response = await apiClient.post<ArtifactDownloadResponse>(
      `/api/v1/procedural-memory/ui/${id}/download`
    )
    return response.data
  },

  downloadBundle: async (
    req: ArtifactBundleRequest
  ): Promise<ArtifactBundleResponse> => {
    const response = await apiClient.post<ArtifactBundleResponse>(
      '/api/v1/procedural-memory/ui/bundle',
      req
    )
    return response.data
  },

  // Version history — mirrors api.prds.listRevisions / getRevision. Authoring
  // history, so the routes refuse a caller below Operator.
  listVersions: async (
    id: string,
    params?: { file_id?: string; skip?: number; limit?: number }
  ): Promise<ArtifactVersionListResponse> => {
    const response = await apiClient.get<ArtifactVersionListResponse>(
      `/api/v1/procedural-memory/ui/${id}/versions`,
      { params }
    )
    return response.data
  },

  getVersion: async (
    id: string,
    versionId: string
  ): Promise<ArtifactVersionResponse> => {
    const response = await apiClient.get<ArtifactVersionResponse>(
      `/api/v1/procedural-memory/ui/${id}/versions/${versionId}`
    )
    return response.data
  },

  // `ifMatch` is REQUIRED — rollback is a mutation; 412 on stale metadata_version.
  rollbackFile: async (
    id: string,
    body: ArtifactRollbackRequest,
    ifMatch: number
  ): Promise<ArtifactResponse> => {
    const response = await apiClient.post<ArtifactResponse>(
      `/api/v1/procedural-memory/ui/${id}/rollback`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  // Bootstrap profiles — the `core` bundle every host receives, and the role
  // profiles a dispatch selects. Create, repin and rollback are a console
  // administrator's decision (403 otherwise); repin and rollback require
  // If-Match (the profile's metadata_version) and return 412 on a stale value.
  // Listing and unlisting members take Operator, because a member list changes
  // only what the next repin would pin.
  profiles: {
    list: async (): Promise<ProfileSummary[]> => {
      const response = await apiClient.get<ProfileSummary[]>(
        '/api/v1/procedural-memory/profiles/'
      )
      return response.data
    },

    // A console administrator's decision (403 otherwise). 201 with the unpinned
    // draft; 409 when the name is taken, a concurrent create included; 422 for
    // a name outside the `role-` pattern.
    create: async (body: ProfileCreate): Promise<ProfileResponse> => {
      const response = await apiClient.post<ProfileResponse>(
        '/api/v1/procedural-memory/profiles/',
        body
      )
      return response.data
    },

    get: async (name: string): Promise<ProfileResponse> => {
      const response = await apiClient.get<ProfileResponse>(
        `/api/v1/procedural-memory/profiles/${encodeURIComponent(name)}/`
      )
      return response.data
    },

    listVersions: async (name: string): Promise<ProfileVersionResponse[]> => {
      const response = await apiClient.get<ProfileVersionResponse[]>(
        `/api/v1/procedural-memory/profiles/${encodeURIComponent(name)}/versions`
      )
      return response.data
    },

    listMembers: async (name: string): Promise<ProfileMembership[]> => {
      const response = await apiClient.get<ProfileMembership[]>(
        `/api/v1/procedural-memory/profiles/${encodeURIComponent(name)}/members`
      )
      return response.data
    },

    // Repeating it answers the same row and changes nothing. 404 for a profile
    // or artefact the caller cannot see; 409 when a repin could never pin the
    // artefact, with `membership_exclusion` in the body naming why.
    addMember: async (
      name: string,
      artifactId: string
    ): Promise<ProfileMembership> => {
      const response = await apiClient.put<ProfileMembership>(
        `/api/v1/procedural-memory/profiles/${encodeURIComponent(name)}/members/${artifactId}`
      )
      return response.data
    },

    // 404 when the artefact is not listed. A pinned member stays pinned until
    // the next repin, which the preview shows leaving.
    removeMember: async (name: string, artifactId: string): Promise<void> => {
      await apiClient.delete(
        `/api/v1/procedural-memory/profiles/${encodeURIComponent(name)}/members/${artifactId}`
      )
    },

    // What a repin would publish now against what the profile pins, with the
    // digest that repin must carry and the metadata_version it is read from.
    repinPreview: async (name: string): Promise<ProfileRepinPreview> => {
      const response = await apiClient.get<ProfileRepinPreview>(
        `/api/v1/procedural-memory/profiles/${encodeURIComponent(name)}/repin-preview`
      )
      return response.data
    },

    // `body.plan_digest` and `ifMatch` are the preview's. 412 on a stale
    // ifMatch; 422 if the header is absent; 409 with `repin_plan: "changed"`
    // when the plan moved after the preview was read.
    repin: async (
      name: string,
      body: ProfileRepinRequest,
      ifMatch: number
    ): Promise<ProfileResponse> => {
      const response = await apiClient.post<ProfileResponse>(
        `/api/v1/procedural-memory/profiles/${encodeURIComponent(name)}/repin`,
        body,
        ifMatchHeaders(ifMatch)
      )
      return response.data
    },

    // 404 on unknown target_bundle_version; 412 on stale ifMatch.
    rollback: async (
      name: string,
      body: ProfileRollbackRequest,
      ifMatch: number
    ): Promise<ProfileResponse> => {
      const response = await apiClient.post<ProfileResponse>(
        `/api/v1/procedural-memory/profiles/${encodeURIComponent(name)}/rollback`,
        body,
        ifMatchHeaders(ifMatch)
      )
      return response.data
    },
  },
}
